<template>
  <div class="pdf_notes_wrp">
    <div class="pdf_notes_header mb-3">
      <div class="row align-items-center">
        <div class="col-auto">
          <h3 class="heading_notes mb-0">Notes</h3>
        </div>
        <div class="col text-right">
          <button
            v-if="viewMode === 'list'"
            class="btn btn-primary"
            @click="switchMode('add')"
          >
            Add Note
          </button>
          <button
            v-else
            class="btn btn-secondary btn-sm"
            @click="switchMode('list')"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <div class="pdf_notes_body">
      <!-- Add Mode -->
      <div v-if="viewMode === 'add'" class="pdf_notes_add_section">
        <div class="form-group">
          <input
            v-model.trim="form.title"
            type="text"
            class="form-control"
            placeholder="Title..."
            autofocus
          />
        </div>
        <div class="form-group pdf_note_editor_wrp">
          <vue-editor
            v-model="form.content"
            placeholder="Description..."
            :editor-toolbar="editorToolbar"
          />
        </div>
        <button
          class="btn btn-primary"
          :disabled="isSubmitting"
          @click="saveNewNote"
        >
          {{ isSubmitting ? 'Saving...' : 'Save' }}
        </button>
      </div>

      <!-- Edit Mode -->
      <div v-else-if="viewMode === 'edit'" class="pdf_notes_add_section">
        <div class="form-group">
          <input
            v-model.trim="form.title"
            type="text"
            class="form-control"
            placeholder="Title..."
            autofocus
            @change="autoSaveNote"
          />
        </div>
        <div class="form-group pdf_note_editor_wrp">
          <vue-editor
            v-model="form.content"
            placeholder="Description..."
            :editor-toolbar="editorToolbar"
            @blur="autoSaveNote"
          />
        </div>
        <button
          class="btn btn-primary"
          :disabled="isSubmitting"
          @click="updateNote"
        >
          {{ isSubmitting ? 'Updating...' : 'Update' }}
        </button>
      </div>

      <!-- List Mode -->
      <div v-else class="pdf_notes_list_section">
        <ul v-if="notes.length > 0" class="pdf_notes_list_items list-unstyled">
          <li v-for="item in notes" :key="item.id">
            <div class="pdf_notes_list_itemwrp" @click="openEditScreen(item)">
              <div class="pdf_notes_title_span">
                <i class="fa fa-pencil" aria-hidden="true"></i>
                <span class="note_html_title">{{ item.title || 'Untitled Note' }}</span>
              </div>
              <span class="pdf_notes_created_date">{{ item.created_at | dateformat }}</span>
            </div>
          </li>
        </ul>
        <div v-else class="text-center pt-5 empty_notes_msg">
          You don't have any notes
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor';

export default {
  name: 'PdfNotes',
  components: {
    VueEditor
  },
  props: {
    pdfIDForNote: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      viewMode: 'list', // 'list' | 'add' | 'edit'
      notes: [],
      isSubmitting: false,
      form: {
        id: null,
        title: '',
        content: ''
      },
      editorToolbar: [['bold', 'italic', 'link']]
    };
  },
  watch: {
    pdfIDForNote: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchNotes();
        }
      }
    }
  },
  methods: {
    async fetchNotes() {
      try {
        const res = await this.$store.dispatch('getNotesToPdfFun', {
          id: this.pdfIDForNote
        });
        const results =
          res &&
          res.data &&
          res.data.data &&
          res.data.data.results;

        this.notes = Array.isArray(results) ? results : [];
      } catch (err) {
        this.notes = [];
      }
    },

    switchMode(mode) {
      this.viewMode = mode;
      if (mode === 'add') {
        this.resetForm();
      }
    },

    resetForm() {
      this.form = {
        id: null,
        title: '',
        content: ''
      };
    },

    openEditScreen(note) {
      this.form = {
        id: note.id,
        title: note.title,
        content: note.data
      };
      this.viewMode = 'edit';
    },

    async saveNewNote() {
      if (!this.form.content && !this.form.title) {
        this.switchMode('list');
        return;
      }

      const payload = {
        pdf_paper: this.pdfIDForNote,
        title: this.form.title,
        data: this.form.content
      };

      this.isSubmitting = true;
      try {
        await this.$store.dispatch('createNewNotesForPdfFun', payload);
        this.resetForm();
        this.switchMode('list');
        await this.fetchNotes();
      } catch (err) {
        // Handle or toast error
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateNote() {
      this.isSubmitting = true;
      try {
        await this.autoSaveNote();
        this.switchMode('list');
        await this.fetchNotes();
      } catch (err) {
        // Handle or toast error
      } finally {
        this.isSubmitting = false;
      }
    },

    autoSaveNote() {
        console.log('Auto-saving note...' , this.form, this.pdfIDForNote);
      if (!this.form.id) return Promise.resolve();

      const payload = {
        pdf_paper: this.pdfIDForNote,
        title: this.form.title,
        data: this.form.content
      };

      return this.$store.dispatch('updateNotesInfoOfPdfFun',{ data: {
        id: this.form.id,
        data: payload
      }});
    }
  }
};
</script>

<style scoped lang="scss">
.pdf_notes_wrp {
  z-index: 1000;
  width: 100%;
  background: #000;
  height: 100dvh;
  padding:75px 10px 0;
  transition: transform ease 0.1s;
  transform: translateX(-270px);

  &.actv {
    transform: translateX(0);
  }

  .pdf_notes_header {
    height: 32px;
  }

  .heading_notes {
    font-size: 20px;
    color: #fff;
    margin-bottom: 15px;
    text-transform: uppercase;
  }

  .form-control {
    border: none;
    background-color: rgba(#ffffff, 0.1);
    color: #ffffff;

    &:focus {
      background-color: rgba(#ffffff, 0.15);
      color: #ffffff;
      box-shadow: none;
    }
  }

  .btn-primary {
    padding: 8px 12px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.12);
    color: #ffd400;
    border: none;
    box-shadow: none;
    line-height: 1;
    font-size: 16px;
    transition: background 0.3s ease, color 0.3s ease;

    &:hover {
      background: #87cefa;
      color: #22292f;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .btn-secondary {
    padding: 6px 10px;
    font-size: 14px;
    background: rgba(255, 255, 255, 0.08);
    border: none;
    color: #ccc;
    box-shadow: none !important;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: #fff;
    }
  }

  .empty_notes_msg {
    font-size: 16px;
    color: #888;
  }

  .pdf_notes_list_items {
    margin: 0;
    padding: 0;

    li {
      margin-bottom: 1px;
      &:last-child {
        margin-bottom: 0;
      }
    }

    .pdf_notes_list_itemwrp {
      background-color: rgba(#ffffff, 0.1);
      padding: 8px 5px 8px 30px;
      color: #fff;
      position: relative;
      cursor: pointer;

      .fa-pencil {
        position: absolute;
        top: 10px;
        left: 8px;
      }

      &:hover {
        background-color: rgba(#ffffff, 0.18);
      }
    }

    .pdf_notes_title_span {
      margin-bottom: 2px;
      line-height: 1;
    }

    .note_html_title {
      font-size: 16px;
      color: #ff8a23;
    }

    .pdf_notes_created_date {
      line-height: 1;
      font-style: italic;
      font-size: 12px;
      color: #aaa;
    }
  }
}
</style>